import { TFunction } from 'i18next';
import { AssetsApiFp } from '@electricjs/core_entity-client';

import {
  AssetManagementTabs,
  AssetManagementTabType,
} from '@/pages/Assets/types';
import { AssetColumnId, AssetColumnIds } from '@/pages/Assets/AssetsTable';
import {
  AssigneeResolverArgs,
  getAssignee,
  isAssigneeHidden,
} from '@/helpers/assignee';
import { EmployeeOption } from '@/components/People/EmployeeGroups/NewEmployeeGroupForm';
import { Asset, AssetAssignmentEmployee } from '@/types/assets';
import { getFullName } from 'common/utils/getFullName';

// Derive the backend's accepted orderBy values directly from the client type so
// this stays in sync automatically when the package is updated.
// The "[3]" is the 4th positional parameter of listAssets (0-indexed):
//   listAssets(organizationId, limit, offset, orderBy, sortOrder, ...)
// There is no named type alias for this union in core_entity-client; extracting
// it structurally via Parameters<> is the only way to avoid copying it by hand.
export type AssetBackendSortField = NonNullable<
  Parameters<ReturnType<typeof AssetsApiFp>['listAssets']>[3]
>;

// Maps each sortable TanStack column ID to its backend field name.
// Only columns the backend actually supports appear here — everything else
// must have enableSorting: false on its column definition in AssetsTable.tsx.
export const columnToBackendSortField: Partial<
  Record<AssetColumnId, AssetBackendSortField>
> = {
  [AssetColumnIds.AssetName]: 'name',
  [AssetColumnIds.CustomAssetId]: 'custom_asset_id',
  [AssetColumnIds.AssetStatus]: 'status',
  [AssetColumnIds.ProductType]: 'product_type.name',
  [AssetColumnIds.AssignedTo]: 'employee.name',
  [AssetColumnIds.SiteName]: 'site.name',
};

export const isValidOrderBy = (orderBy: string): orderBy is AssetColumnId =>
  orderBy in columnToBackendSortField;

export const mapOrderByToBackendField = (
  orderBy: string
): AssetBackendSortField =>
  columnToBackendSortField[orderBy as AssetColumnId] ?? 'name';

export const isValidAssetManagementTab = (
  tab: string | null
): tab is AssetManagementTabType => {
  return Object.values(AssetManagementTabs).includes(
    tab as AssetManagementTabType
  );
};

/** Assignee cell for the assets CSV: the same visibility rule as the table. */
export const getAssigneeCsvName = (
  asset: Asset,
  t: TFunction,
  assigneeResolver: AssigneeResolverArgs
) => {
  if (isAssigneeHidden(asset, assigneeResolver)) {
    return t('common:restrictedEmployee.label');
  }

  const assignee = getAssignee(asset, assigneeResolver);
  return assignee ? getFullName(assignee.firstName, assignee.lastName) : '';
};

type AssignedToUpdateArgs = {
  selectedId: string | undefined;
  currentId: string | null | undefined;
  isCurrentSelectable: boolean;
};

/**
 * Value of `assignedTo` for the update payload: undefined leaves the assignee
 * untouched, null unassigns. A current assignee missing from the form options
 * (offboarded, or outside the user's divisions) is never loaded into the form,
 * so its empty field must not be sent as an unassign.
 */
export const getAssignedToUpdate = ({
  selectedId,
  currentId,
  isCurrentSelectable,
}: AssignedToUpdateArgs) => {
  if (!selectedId && currentId && !isCurrentSelectable) {
    return undefined;
  }

  return selectedId !== (currentId ?? undefined)
    ? selectedId || null
    : undefined;
};

/**
 * Option that represents the asset's current assignee in the edit form. The
 * dropdown is division-scoped by the backend, so an assignee in it is always
 * visible. Otherwise an inactive but visible assignee (resolved elsewhere) gets
 * a minimal option of their own; undefined means the assignee can't be
 * resolved (unassigned, or hidden by divisions).
 */
export const getCurrentAssigneeOption = (
  currentId: string | null | undefined,
  assignee: AssetAssignmentEmployee | undefined,
  employeeOptions: EmployeeOption[]
): EmployeeOption | undefined => {
  const inDropdown = employeeOptions.find(option => option.value === currentId);
  if (inDropdown || !assignee) {
    return inDropdown;
  }

  const name = getFullName(assignee.firstName, assignee.lastName);
  return {
    value: assignee.id,
    label: name,
    name,
    jobTitle: '',
    email: '',
  };
};
